import React, { useMemo, useState } from "react";

import { GetDoc } from "@/Utils/StateLib";
import { IsRefreshable } from "@/Utils/Misc";
import { useBackgroundSync } from "@/hooks/useBackgroundSync";
import { useLevelsSync } from "@/hooks/useLevelsSync";
import { useTopOfChartSync } from "@/hooks/useTopOfChartSync";
import { useTopic } from "@/hooks/useTopic";
import { useZoomSync } from "@/hooks/useZoomSync";
import { SubscribeExternalModuleReactAPI } from "@coreapp/main";

const ChartDocumentContext = React.createContext();

const ChartDocumentProvider = ({ children }) => {
  const [isRefreshable, setRefreshable] = useState(false);
  const [chartDocument, setChartDocument] = useState(() => ({
    state: GetDoc()
  }));

  useTopic("chartMetadataLoaded", metadata => {
    const nextRefreshable = IsRefreshable(metadata);
    setRefreshable(nextRefreshable);
  });

  React.useEffect(() => {
    SubscribeExternalModuleReactAPI(setChartDocument, "doc");
  }, []);

  const contextValue = useMemo(
    () => ({ chartDocument, setChartDocument, isRefreshable }),
    [chartDocument, isRefreshable]
  );

  return (
    <ChartDocumentContext.Provider value={contextValue}>
      <ChartDocumentUrlSync />
      {children}
    </ChartDocumentContext.Provider>
  );
};

/**
 * Renders inside the `ChartDocumentContext.Provider` so URL → coreapp sync
 * hooks that need `chartDocument` (like useTopOfChartSync) can read it via
 * `useChartDocumentContext()`. useBackgroundSync uses the chartDocumentLoaded
 * topic instead of context to avoid re-firing on every UpdateDocContext call.
 */
const ChartDocumentUrlSync = () => {
  useBackgroundSync();
  useTopOfChartSync();
  useLevelsSync();
  useZoomSync();
  return null;
};

export const useChartDocumentContext = () => React.useContext(ChartDocumentContext);

export default ChartDocumentProvider;
